import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { CirclePlus, Edit, Clock, Menu, Download, ChevronDown, FileText, FileSpreadsheet } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setApiErrorJson, setApiJson } from '../../../redux/Actions/ApiAction';
import { getSelectedUnit } from '../../../storage/Storage';
import { addKiosParent, searchKiosParent, updateKiosParent, deleteKiosParent, addKiosChild, searchKiosChild, updateKiosChild, deleteKiosChild } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import PageHeader from '../../../PageHeader/PageHeader';
import EditButton from '../../../components/Edit/EditButton';
import DeleteButton from '../../../components/Delete/DeleteButton';
import GroupPagination from '../../../components/Pagination/GroupPagination';
import { setGroupPagination } from '../../../redux/Actions/GroupPaginationAction';
import ServicePagination from '../../../components/Pagination/ServicePagination';
import { setServicePagination } from '../../../redux/Actions/ServicePaginationAction';
import toast from 'react-hot-toast';
import { hasPermission } from '../../../utils/permissionUtils';
import DeleteConfirmationModal from '../../../components/CustomModal/DeleteConfirmationModal';
import { exportToCSV, exportToPDF } from '../../../utils/exportUtils';

function KioskServices() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const ApiReducer = useSelector((state) => state.ApiReducer);
    const GroupPaginationReducer = useSelector((state) => state.GropPaginationReducer);
    const ServicePaginationReducer = useSelector((state) => state.ServicePaginationReducer);

    const [parentService, setParentService] = useState();
    const [childService, setChildService] = useState();
    const [parentId, setParentId] = useState('');
    const [selectedParent, setSelectedParent] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Export dropdown state
    const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
    const exportDropdownRef = useRef(null);

    const canEditService = hasPermission('services', 'write');
    const canReadService = hasPermission('services', 'read');
    const canDeleteService = hasPermission('services', 'delete');
    const canAddService = hasPermission('services', 'write');

    useEffect(() => {
        getKiosGroups();
    }, [GroupPaginationReducer.pagination.page, GroupPaginationReducer.pagination.limit]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
                setIsExportDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (selectedParent) {
            showSelectedChild(selectedParent);
        }
    }, [ServicePaginationReducer.pagination.page, ServicePaginationReducer.pagination.limit]);

    const getKiosGroups = () => {
        const json = {
            page: GroupPaginationReducer.pagination.page,
            limit: GroupPaginationReducer.pagination.limit,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchKiosParent).then((res) => {
            if (res?.data?.docs) {
                setParentService(res.data?.docs);
                dispatch(setGroupPagination({
                    total: res?.data?.totalDocs,
                    page: GroupPaginationReducer.pagination.page,
                    limit: GroupPaginationReducer.pagination.limit
                }));
            } else {
                setParentService([]);
                toast.error('Failed to fetch kios groups. Please try again.');
            }
        }).catch((err) => {
            setParentService([]);
            toast.error('Error fetching kios groups. Please try again.');
            console.error(err);
        });
    };

    const showSelectedChild = (parent) => {
        setParentId(parent?._id);
        setSelectedParent(parent);

        const json = {
            page: ServicePaginationReducer.pagination.page,
            limit: ServicePaginationReducer.pagination.limit,
            search: { parentId: parent?._id }
        };

        HitApi(json, searchKiosChild).then((res) => {
            if (res?.data?.docs) {
                dispatch(setServicePagination({
                    total: res?.data?.totalDocs || 0,
                    page: ServicePaginationReducer.pagination.page,
                    limit: ServicePaginationReducer.pagination.limit
                }));
                setChildService(res.data?.docs);
            } else {
                setChildService([]);
                toast.error('Failed to fetch kios services. Please try again.');
            }
        }).catch((err) => {
            setChildService([]);
            toast.error('Error fetching kios services. Please try again.');
            console.error(err);
        });
    };

    const handleEdit = (row) => {
        if (row?._id) navigate(`/kiosParent/edit/${row._id}`);
        else toast.error('Unable to edit: Kios group ID not found');
    };

    const handleEditChild = (row) => {
        if (row?._id) navigate(`/editKiosChild/${row._id}`);
        else toast.error('Unable to edit: Kios service ID not found');
    };

    const handleDelete = (row) => {
        setDeletingRow(row);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        setIsDeleting(true);
        const json = { _id: deletingRow?._id };

        HitApi(json, deleteKiosParent).then((res) => {
            if (res?.success || res?.message?.includes('deleted successfully')) {
                toast.success('Kios group deleted successfully!');
                getKiosGroups();

                if (selectedParent?._id === deletingRow?._id) {
                    setChildService(null);
                    setSelectedParent(null);
                    setParentId('');
                }
            } else {
                toast.error(res?.message || 'Failed to delete kios group');
            }
        }).catch((err) => {
            toast.error('Error occurred while deleting kios group. Please try again.');
            console.error(err);
        }).finally(() => {
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setDeletingRow(null);
        });
    };

    const handleDeleteChild = (row) => {
        const serviceName = row.name || 'this kios service';

        if (window.confirm(`Are you sure you want to delete ${serviceName}?`)) {
            const json = { _id: row?._id };
            HitApi(json, deleteKiosChild).then((res) => {
                if (res?.success || res?.message?.includes('deleted successfully')) {
                    toast.success('Kios service deleted successfully!');
                    showSelectedChild(selectedParent);
                } else {
                    toast.error(res?.message || 'Failed to delete kios service');
                }
            }).catch((err) => {
                toast.error('Error occurred while deleting kios service. Please try again.');
                console.error(err);
            });
        }
    };

    const getRandomColor = (index) => {
        const colors = [
            'border-l-blue-500', 'border-l-green-500', 'border-l-yellow-500',
            'border-l-red-500', 'border-l-purple-500', 'border-l-pink-500',
            'border-l-indigo-500', 'border-l-teal-500'
        ];
        return colors[index % colors.length];
    };

    const formatServiceTime = (serviceTime) => {
        if (!serviceTime) return '00:00';
        return serviceTime;
    };

    const NullApiJsonORErrorJson = () => {
        dispatch(setApiJson(null));
        dispatch(setApiErrorJson(null));
    };

    // Export All Kios Services
    const handleExportAllServices = async (format) => {
        setIsExportDropdownOpen(false);

        try {
            toast.loading('Preparing export...');

            // Fetch all kios parents
            const parentJson = {
                page: 1,
                limit: 100000,
                search: {
                    unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
                }
            };

            const parentRes = await HitApi(parentJson, searchKiosParent);

            if (!parentRes?.data?.docs || parentRes.data.docs.length === 0) {
                toast.dismiss();
                toast.error('No kios groups to export');
                return;
            }

            const allParents = parentRes.data.docs;
            const exportData = [];
            let serialNumber = 1;

            // Fetch children for each parent
            for (const parent of allParents) {
                const childJson = {
                    page: 1,
                    limit: 100000,
                    search: { parentId: parent._id }
                };

                const childRes = await HitApi(childJson, searchKiosChild);
                const children = childRes?.data?.docs || [];

                if (children.length > 0) {
                    // Add each child with parent info
                    children.forEach((child) => {
                        exportData.push({
                            serialNumber: serialNumber++,
                            groupName: parent.name || 'N/A',
                            serviceName: child.name || 'N/A',
                            price: child.price ? `Rs. ${Number(child.price).toFixed(2)}` : 'Rs. 0.00',
                            duration: formatServiceTime(child.service_time),
                            status: child.status || 'Active',
                            createdAt: child.createdAt ? new Date(child.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                            }) : 'N/A'
                        });
                    });
                } else {
                    // Add parent with no services
                    exportData.push({
                        serialNumber: serialNumber++,
                        groupName: parent.name || 'N/A',
                        serviceName: 'No services',
                        price: '-',
                        duration: '-',
                        status: parent.status || 'Active',
                        createdAt: parent.createdAt ? new Date(parent.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                        }) : 'N/A'
                    });
                }
            }

            if (exportData.length === 0) {
                toast.dismiss();
                toast.error('No data to export');
                return;
            }

            const exportHeaders = [
                { title: 'S.No', key: 'serialNumber' },
                { title: 'Group Name', key: 'groupName' },
                { title: 'Service Name', key: 'serviceName' },
                { title: 'Price', key: 'price' },
                { title: 'Duration', key: 'duration' },
                { title: 'Status', key: 'status' },
                { title: 'Created At', key: 'createdAt' }
            ];

            const filename = `kios_services_export_${new Date().toISOString().split('T')[0]}`;

            toast.dismiss();

            if (format === 'csv') {
                exportToCSV(exportData, exportHeaders, `${filename}.csv`);
                toast.success(`Exported ${exportData.length} kios services to CSV`);
            } else if (format === 'pdf') {
                exportToPDF(exportData, exportHeaders, `${filename}.pdf`, 'Kios Services Report');
                toast.success(`Exported ${exportData.length} kios services to PDF`);
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <div className="flex items-center justify-between mb-4">
                <PageHeader title={'Kios Services Management'} description={'View and manage kios service categories'} />

                {/* Export Data Dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                    <button
                        onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-medium"
                    >
                        <Download size={16} />
                        Export Data
                        <ChevronDown size={16} className={`transition-transform ${isExportDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isExportDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <button
                                onClick={() => handleExportAllServices('csv')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700"
                            >
                                <FileSpreadsheet size={18} className="text-green-600" />
                                Export as CSV
                            </button>
                            <button
                                onClick={() => handleExportAllServices('pdf')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700 border-t border-gray-100"
                            >
                                <FileText size={18} className="text-red-600" />
                                Export as PDF
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <div className="w-80 bg-white border-r min-h-screen">
                    <div className="p-4 border-b bg-gray-50">
                        {canAddService && (
                            <NavLink to="/addKiosParent">
                                <button className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                    <CirclePlus size={16} />
                                    Add Kios Group
                                </button>
                            </NavLink>
                        )}
                    </div>

                    <div className="p-2">
                        {parentService?.map((parent, index) => (
                            <div
                                key={parent._id}
                                className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedParent?._id === parent._id
                                    ? 'bg-blue-50 border-l-4 border-black'
                                    : 'hover:bg-gray-50'
                                    }`}
                                onClick={() => showSelectedChild(parent)}
                            >
                                <div className="flex items-center gap-3">
                                    <Menu size={16} className="text-gray-400" />
                                    <span className="font-medium text-gray-900">{parent.name}</span>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                    {canEditService && (
                                        <EditButton onEdit={() => handleEdit(parent)} itemName="Kios Group" />
                                    )}
                                    {canDeleteService && (
                                        <DeleteButton onDelete={() => handleDelete(parent)} itemName="Kios Group" />
                                    )}
                                </div>
                            </div>
                        ))}

                        <div className='flex justify-end p-3'>
                            <GroupPagination />
                        </div>
                    </div>
                </div>

                {/* Right Panel */}
                <div className="flex-1 bg-white">
                    {childService ? (
                        <div>
                            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {selectedParent?.name} Services
                                </h2>
                                {canAddService && (
                                    <NavLink to={`/addKiosChild/${parentId}`} onClick={NullApiJsonORErrorJson}>
                                        <button className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                            <CirclePlus size={16} />
                                            Add Kios Service
                                        </button>
                                    </NavLink>
                                )}
                            </div>

                            <div className="p-6 space-y-2">
                                {childService.length > 0 ? (
                                    childService.map((child, index) => (
                                        <div
                                            key={child._id}
                                            className={`group flex items-center justify-between p-4 rounded-lg border-l-4 ${getRandomColor(index)} bg-gray-50 hover:bg-gray-100`}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="font-medium text-gray-900">{child.name}</h3>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <div className="font-semibold text-gray-900">
                                                                Rs. {child.price?.toLocaleString() || '0'}
                                                            </div>
                                                            {child.member_price && (
                                                                <div className="text-sm text-gray-500">
                                                                    Member: Rs. {child.member_price.toLocaleString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                                            <Clock size={14} />
                                                            <span>{formatServiceTime(child.service_time)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                                            {canEditService && (
                                                                <EditButton onEdit={() => handleEditChild(child)} itemName="Kios Service" />
                                                            )}
                                                            {canDeleteService && (
                                                                <DeleteButton onDelete={() => handleDeleteChild(child)} itemName="Kios Service" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-600">No kios services found in this category</div>
                                )}
                            </div>

                            <div className="p-4 flex justify-end">
                                <ServicePagination />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-600">
                            Select a kios group to view services
                        </div>
                    )}
                </div>
            </div>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingRow(null);
                }}
                onConfirm={confirmDelete}
                itemName={deletingRow?.name}
                itemType="kios group"
                isLoading={isDeleting}
                warningMessage="This will also delete all kios services related to this group."
            />
        </div>
    );
}

export default KioskServices;
